import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { backendUrl, detectCommrayEnvironment, getBackendBaseUrl } from "../utils/backendUrl";
import { initSession } from "../utils/sessionManager";
import { useAuth } from "../context/AuthContext";
import UserTimezoneSelect from "./UserTimezoneSelect";
import MfaLoginStep from "./MfaLoginStep";
import "./Login.css";

function Login({ onLoginSuccess }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [errorCode, setErrorCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [subscribeLoading, setSubscribeLoading] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState("starter");
  const [plans, setPlans] = useState([]);
  const [mfaChallenge, setMfaChallenge] = useState("");
  const navigate = useNavigate();
  const { loginWithResponse } = useAuth();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(backendUrl("/api/billing/plans"), { withCredentials: true });
        if (!cancelled && Array.isArray(res.data?.plans) && res.data.plans.length > 0) {
          setPlans(res.data.plans);
          setSelectedPlan(res.data.plans[0].id);
        }
      } catch {
        // Plans are optional on the login screen
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleLogin = async () => {
    setLoading(true);
    setError("");
    setErrorCode("");

    try {
      const res = await axios.post(backendUrl("/auth/login"), {
        username,
        password,
      }, {
        withCredentials: true,
        timeout: 20000,
      });

      if (res.data?.mfaRequired && res.data?.mfaChallengeToken) {
        setMfaChallenge(res.data.mfaChallengeToken);
        setLoading(false);
        return;
      }

      if (res.data.username || res.data.user || res.data.token) {
        if (res.data.token) {
          localStorage.setItem("token", res.data.token);
        }
        if (res.data.role) localStorage.setItem("userRole", res.data.role);
        if (res.data.username) localStorage.setItem("username", res.data.username);
        // Initialize session (track activity for 15-minute timeout)
        initSession();
        console.log("Login successful, navigating to dashboards...");
        await loginWithResponse(res.data);
        if (onLoginSuccess) {
          onLoginSuccess(res.data);
        }
        navigate("/dashboards", { replace: true });
      } else {
        console.error("Invalid response from server:", res.data);
        setError("Invalid response from server. Please try again.");
        setLoading(false);
      }
    } catch (err) {
      console.error("Login error:", err);
      const apiBase = getBackendBaseUrl();
      const isLocal = detectCommrayEnvironment() === "local";
      if (err.response && err.response.data && err.response.data.error) {
        setError(err.response.data.error);
        setErrorCode(err.response.data.code || "");
      } else if (err.response && err.response.status === 401) {
        setError("Invalid username or password. Please try again.");
      } else if (err.response && err.response.status === 403) {
        setError(err.response.data?.error || "Access denied. Please update billing to continue.");
        setErrorCode(err.response.data?.code || "SUBSCRIPTION_REQUIRED");
      } else if (err.response && err.response.status === 400) {
        setError(err.response.data?.error || "Invalid input. Please check your credentials.");
      } else if (err.response && err.response.status >= 500) {
        setError(
          isLocal
            ? "The API returned a server error. Confirm the backend is running on port 8080."
            : `The Commray API is unavailable (${apiBase || "api"}). Try again in a minute or contact support if this continues.`
        );
      } else if (
        !err.response &&
        (err.code === "ECONNABORTED" || err.message?.toLowerCase().includes("timeout"))
      ) {
        setError(
          "Login is taking too long. The API may be busy — wait a few seconds and try again."
        );
      } else if (
        !err.response &&
        (err.code === "ECONNREFUSED" ||
          err.message?.includes("Network Error") ||
          err.message?.includes("ECONNREFUSED"))
      ) {
        setError(
          isLocal
            ? "Cannot connect to the backend. From the project backend folder run: mvn spring-boot:run — the API must listen on port 8080 while you use npm start."
            : `Cannot reach the Commray API at ${apiBase || "the configured API URL"}. The test API may be restarting — try again shortly.`
        );
      } else {
        setError("Invalid credentials. Please try again.");
      }
      setLoading(false);
    }
  };

  const handleSubscribe = async () => {
    if (!username.trim() || !password) {
      setError("Enter your username and password, then click Subscribe.");
      return;
    }
    setSubscribeLoading(true);
    setError("");
    try {
      const res = await axios.post(
        backendUrl("/api/billing/resume-checkout"),
        { username, password, plan: selectedPlan || "starter" },
        { withCredentials: true }
      );
      if (res.data?.url) {
        window.location.href = res.data.url;
        return;
      }
      setError("Checkout did not return a URL. Please try again.");
    } catch (err) {
      setError(
        err.response?.data?.error ||
          "Unable to start subscription checkout. Check your credentials and try again."
      );
      setErrorCode(err.response?.data?.code || "");
    } finally {
      setSubscribeLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleLogin();
    }
  };

  if (mfaChallenge) {
    return (
      <div className="login-container">
        <div className="login-box">
          <MfaLoginStep
            challengeToken={mfaChallenge}
            onSuccess={async (data) => {
              if (data?.token) localStorage.setItem("token", data.token);
              if (data?.role) localStorage.setItem("userRole", data.role);
              if (data?.username) localStorage.setItem("username", data.username);
              initSession();
              await loginWithResponse(data);
              if (onLoginSuccess) onLoginSuccess(data);
              navigate("/dashboards", { replace: true });
            }}
            onCancel={() => {
              setMfaChallenge("");
              setError("");
              setLoading(false);
            }}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="login-container">
      <div className="login-box">
        <h2>Commray Login</h2>
        <p className="subtitle">Welcome back to the monitoring system</p>
        
        {error && <div className="error-message">{error}</div>}

        <div className="form-group">
          <label htmlFor="commray-login-tz">Time zone</label>
          <UserTimezoneSelect showInnerLabel={false} selectId="commray-login-tz" embedded />
        </div>
        
        <form onSubmit={(e) => { e.preventDefault(); handleLogin(); }}>
          <div className="form-group">
            <label htmlFor="username">Username</label>
            <input 
              id="username"
              type="text"
              placeholder="Enter your username" 
              value={username} 
              onChange={(e) => setUsername(e.target.value)}
              onKeyPress={handleKeyPress}
              disabled={loading}
              required
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="password">Password</label>
            <input 
              id="password"
              type="password" 
              placeholder="Enter your password" 
              value={password} 
              onChange={(e) => setPassword(e.target.value)}
              onKeyPress={handleKeyPress}
              disabled={loading}
              required
            />
          </div>
          
          <button 
            type="submit"
            disabled={loading} 
            className="login-button"
          >
            {loading ? (
              <>
                <div className="loading-spinner"></div>
                Logging in...
              </>
            ) : (
              'Login'
            )}
          </button>
        </form>

        {errorCode === "SUBSCRIPTION_REQUIRED" && (
          <div className="subscribe-panel" style={{ marginTop: "1rem", textAlign: "left" }}>
            <p style={{ marginBottom: "0.75rem" }}>
              Update billing with the same username and password to restore paid access.
            </p>
            {plans.length > 0 && (
              <div className="form-group">
                <label htmlFor="subscribe-plan">Plan</label>
                <select
                  id="subscribe-plan"
                  value={selectedPlan}
                  onChange={(e) => setSelectedPlan(e.target.value)}
                  disabled={subscribeLoading}
                >
                  {plans.map((plan) => (
                    <option key={plan.id} value={plan.id}>
                      {plan.name}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <button
              type="button"
              className="login-button"
              onClick={handleSubscribe}
              disabled={subscribeLoading || loading}
            >
              {subscribeLoading ? "Starting checkout..." : "Subscribe with Stripe"}
            </button>
          </div>
        )}
        
        <div className="signup-link">
          <p>Don't have an account? <Link to="/signup">Sign up here</Link></p>
          <p>Forgot your password? <Link to="/reset-password">Reset password</Link></p>
        </div>
      </div>
    </div>
  );
}

export default Login;